/* eslint-disable camelcase */
import Component from '../../Component';
import ToggleSwitch, { ToggleSwitchState } from '../toggle-switch';
import AdvancedSelect, {
	AdvancedSelectOption,
	AdvancedSelectState,
} from '../advanced-select';
import {
	billsData,
	BillsResponseData,
	DEFAULT_SORT_PARAMS,
	usTopologyData,
} from './source';

export type ActiveViewOptions = 'map' | 'table';

export type ActiveLegislationTypeOptions = 'bills' | 'laws';

export interface BillTrackerFiltersState {
	view: ActiveViewOptions;
	legislationType: ActiveLegislationTypeOptions;
	state: string;
	categories: Array<string>;
	sort: string[];
}

export const defaultFilters = {
	state: 'all',
	categories: ['all'],
	sort: DEFAULT_SORT_PARAMS.map(({ column, order }) => `${column}:${order}`),
};

class BillTrackerFilters extends Component<HTMLElement> {
	static selector = '.bill-tracker__filters';

	togglesWrapper?: HTMLElement;
	resetButton?: HTMLButtonElement;
	categoryGroups: BillsResponseData['categoryGroups'] = {};

	state = {
		view: '',
		legislationType: 'bill',
		...defaultFilters,
	};

	viewFilter?: ToggleSwitch;
	legislationTypeFilter?: ToggleSwitch;
	stateFilter?: AdvancedSelect;
	categoriesFilter?: AdvancedSelect;
	sortFilter?: AdvancedSelect;

	async afterInit() {
		const { categoryGroups } = await billsData();

		this.categoryGroups = categoryGroups;
		this.togglesWrapper = Object.assign(document.createElement('div'), {
			className: 'bill-tracker__toggles',
		});

		this.element.appendChild(this.togglesWrapper);

		this.initializeViewFilter();
		this.initializeLegislationTypeFilter();
		await this.initializeStateFilter();
		this.initializeCategoriesFilter();
		await this.initializeSortFilter();
		this.initializeReset();

		this.emit('init', this);
	}

	initializeViewFilter() {
		this.viewFilter = ToggleSwitch.create({
			leftOption: {
				value: 'map',
				label: 'Map View',
			},
			rightOption: {
				value: 'list',
				label: 'List View',
			},
		}) as ToggleSwitch;

		this.viewFilter.onStateChange(
			({ currentValue: view }: ToggleSwitchState) =>
				this.setState({ view }),
			true
		);

		this.togglesWrapper?.appendChild(this.viewFilter.element);
	}

	initializeLegislationTypeFilter() {
		this.legislationTypeFilter = ToggleSwitch.create({
			leftOption: {
				value: 'bill',
				label: 'Active Bills',
			},
			rightOption: {
				value: 'law',
				label: 'Current Laws',
			},
		}) as ToggleSwitch;

		this.legislationTypeFilter.onStateChange(
			({ currentValue: legislationType }: ToggleSwitchState) =>
				this.setState({ legislationType }),
			true
		);

		this.togglesWrapper?.appendChild(this.legislationTypeFilter.element);
	}

	async initializeSortFilter() {
		const sortOptions: AdvancedSelectOption[] = [
			{
				value: 'state:ascending',
				label: 'Sort By: State, Alphabetical',
			},
			{
				value: 'state:descending',
				label: 'Sort By: State, Descending',
			},
			{
				value: 'action_date:descending',
				label: 'Sort By: Action Date, Most Recent',
			},
			{
				value: 'action_date:ascending',
				label: 'Sort By: Action Date, Oldest',
			},
		];

		this.sortFilter = AdvancedSelect.create({
			className: 'bill-tracker__filter bill-tracker__filter--sort',
			defaultOptions: DEFAULT_SORT_PARAMS.length
				? DEFAULT_SORT_PARAMS.map(
						({ column, order }) => `${column}:${order}`
				  )
				: false,
			options: sortOptions,
			optionRenderer(option) {
				return `
					<li data-option role="button" data-value="${option.value}" class="trevor-ti-check">${option.label}</li>
				`;
			},
			onButtonLabelUpdate(selections) {
				// Show only the first sort selection
				return selections[0]?.label || 'Please select';
			},
		}) as AdvancedSelect;

		this.element.appendChild(this.sortFilter.element as HTMLElement);

		this.sortFilter.onStateChange(
			({ selectedOptions }: AdvancedSelectState) =>
				this.setState({
					sort: selectedOptions.map(({ value }) => value),
				})
		);
	}

	async initializeStateFilter() {
		const usData = await usTopologyData();
		const statesOptions: AdvancedSelectOption[] =
			usData.objects.default.geometries.map(({ properties }: any) => ({
				value: properties['hc-a2'],
				label: properties['woe-name'],
			}));

		// Sort alphabetically
		statesOptions.sort((stateA, stateB) =>
			stateA.label > stateB.label ? 1 : -1
		);

		// Prepend "All" selection
		statesOptions.unshift({
			value: 'all',
			label: 'All States',
		});

		this.stateFilter = AdvancedSelect.create({
			className: 'bill-tracker__filter bill-tracker__filter--states',
			options: statesOptions,
			optionRenderer(option) {
				return `
					<li data-option role="button" data-value="${option.value}" >${option.label}</li>
				`;
			},
		}) as AdvancedSelect;

		this.element.appendChild(this.stateFilter.element);

		this.stateFilter.onStateChange(
			({ selectedOptions }: AdvancedSelectState) => {
				if (selectedOptions?.length) {
					this.setState({
						state: selectedOptions[0]?.value || 'all',
					});
				}
			},
			true
		);
	}

	async initializeCategoriesFilter() {
		const options = [
			{ value: 'all', label: 'All Categories' },
			// Pro categories
			{
				value: 'allPro',
				label: this.categoryGroups.pro.select_all_label,
			},
			...Object.entries(this.categoryGroups.pro.categories)
				.filter(([, { hide_in_filters }]) => !hide_in_filters)
				.map(([value, categoryData]) => ({
					value,
					label: categoryData.title as string,
					data: categoryData,
				})),
			// Anti categories
			{
				value: 'allAnti',
				label: this.categoryGroups.anti.select_all_label,
			},
			...Object.entries(this.categoryGroups.anti.categories)
				.filter(([, { hide_in_filters }]) => !hide_in_filters)
				.map(([value, categoryData]) => ({
					value,
					label: categoryData.title as string,
					data: categoryData,
				})),
		];

		this.categoriesFilter = AdvancedSelect.create({
			className: 'bill-tracker__filter bill-tracker__filter--categories',
			multiple: true,
			options,
			optionsListRenderer: (_options, optionRenderer = () => '') => {
				const allOption = _options.find(
					({ value }) => value === 'all'
				) as AdvancedSelectOption;
				const proOptions = _options.filter(
					({ value }) =>
						value === 'allPro' ||
						value in this.categoryGroups.pro.categories
				);
				const antiOptions: AdvancedSelectOption[] = _options.filter(
					({ value }) =>
						value === 'allAnti' ||
						value in this.categoryGroups.anti.categories
				);

				return `
				<ul class="advanced-select__options" data-options-list>
					${optionRenderer(allOption)}
					<li class="bill-tracker__suboptions">
						<p class="bill-tracker__suboptions-heading">${
							this.categoryGroups.pro.heading
						}</p>
						<ul>${proOptions.map((_option) => optionRenderer(_option)).join('')}</ul>
					</li>
					<li class="bill-tracker__suboptions">
						<p class="bill-tracker__suboptions-heading">${
							this.categoryGroups.anti.heading
						}</p>
						<ul>${antiOptions.map(optionRenderer).join('')}</ul>
					</li>
				</ul>`;
			},
			optionRenderer(option) {
				return `
					<li data-option data-value="${option.value}" >
						${
							option.value !== `all`
								? `<div class="bill-tracker__filter-checkbox trevor-ti-check"></div>`
								: ''
						}
						<span>${option.label}</span>
					</li>
				`;
			},
			onOptionClick: this.onCategoryOptionClick.bind(this),
			onButtonLabelUpdate: this.onCategoryButtonLabelUpdate.bind(this),
		}) as AdvancedSelect;

		this.element.appendChild(this.categoriesFilter.element);

		this.categoriesFilter.onStateChange(
			({ selectedOptions }: AdvancedSelectState) => {
				if (selectedOptions) {
					this.setState({
						categories: selectedOptions.map(({ value }) => value),
					});
				}
			},
			true
		);
	}

	initializeReset() {
		this.resetButton = Object.assign(document.createElement('button'), {
			className: 'bill-tracker__reset hidden',
			type: 'button',
			textContent: 'Reset Filters',
		});

		this.resetButton.addEventListener('click', this.reset.bind(this));

		this.element.appendChild(this.resetButton);
	}

	onCategoryOptionClick(
		clickedOptionValue: string,
		currentSelection: AdvancedSelectOption[],
		allOptions: AdvancedSelectOption[]
	) {
		if (clickedOptionValue === 'all') {
			return allOptions.filter(({ value }) => value === 'all');
		}

		let newSelections = [];

		const isOptionSelected = currentSelection.some(
			({ value }) => value === clickedOptionValue
		);

		/* eslint-disable-next-line @wordpress/no-unused-vars-before-return */
		const currentSelectionValues = currentSelection
			.map(({ value }) => value)
			.filter((value) => value !== 'all');

		// If clicking on "allPro" or "allAnti"
		if (/all(pro|anti)/i.test(clickedOptionValue)) {
			const groupType = clickedOptionValue === 'allPro' ? 'pro' : 'anti';
			const categoriesInGroup = Object.keys(
				this.categoryGroups[groupType].categories
			);

			if (isOptionSelected) {
				// If selected, remove all options in this group
				newSelections = currentSelection.filter(
					({ value }) =>
						value !== clickedOptionValue &&
						!categoriesInGroup.includes(value as string) &&
						value !== 'all'
				);
			} else {
				// Otherwise, include these categories into the selection
				newSelections = allOptions.filter(
					({ value }) =>
						(categoriesInGroup.includes(value as string) ||
							currentSelectionValues.includes(value) ||
							value === clickedOptionValue) &&
						value !== 'all'
				);
			}
		} else {
			// If clicking on single

			const groupSelector =
				clickedOptionValue in this.categoryGroups.pro.categories
					? 'allPro'
					: 'allAnti';

			// Otherwise,
			newSelections = allOptions.filter(({ value }) => {
				// Toggle clicked option
				if (value === clickedOptionValue) {
					return !currentSelectionValues.includes(clickedOptionValue);
				}
				// Remove corresponding "all*" selection
				if (value === groupSelector) {
					return false;
				}

				// Include current selections
				return currentSelectionValues.includes(value as string);
			});
		}

		// Select 'All' if selection is empty
		if (!newSelections.length) {
			newSelections.push(
				allOptions.find(
					({ value }) => value === 'all'
				) as AdvancedSelectOption
			);
		}

		return newSelections;
	}

	onCategoryButtonLabelUpdate(currentSelections: AdvancedSelectOption[]) {
		if (currentSelections.length === 0) {
			return 'Please select';
		}

		if (currentSelections.length === 1) {
			return currentSelections[0].label;
		}

		// Remove all* selectors
		const filteredSelections = currentSelections.filter(
			({ value }) => !/all/.test(value as string)
		);

		return `${filteredSelections[0].label} + ${
			currentSelections.length - 1
		}`;
	}

	reset() {
		this.stateFilter?.select(['all']);
		this.categoriesFilter?.select(['all']);
		this.sortFilter?.select(defaultFilters.sort);
	}

	setFilters(filterData: BillTrackerFiltersState) {
		Object.entries(filterData).forEach(([filterKey, filterValue]) => {
			switch (filterKey) {
				case 'view':
					this.viewFilter?.setState({ currentValue: filterValue });
					break;
				case 'legislationType':
					this.legislationTypeFilter?.setState({
						currentValue: filterValue,
					});
					break;
				case 'state':
					this.stateFilter?.select(filterValue);
					break;
				case 'categories':
					this.categoriesFilter?.select(filterValue, true);
					break;
				case 'sort':
					this.sortFilter?.select(filterValue);
					break;
			}
		});
	}

	componentDidUpdate() {
		const { state, view, categories, sort } = this.state;

		const willShowReset =
			(categories.length && categories[0] !== 'all') ||
			(state !== 'all' && view !== 'map') ||
			DEFAULT_SORT_PARAMS.some(
				({ column, order }) => !sort.includes(`${column}:${order}`)
			);

		this.resetButton?.classList.toggle('hidden', !willShowReset as boolean);
	}
}

export default BillTrackerFilters;
